// v3E.3 — "Share with my doctor" button.
// Opens a sheet that lists the user's emergency QR public link + a "Copy link" + "Print" CTA.
// Reused on /health and /consultations/:id.
import React, { useEffect, useState } from "react";
import { Share2, Copy, Printer, X, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api/client";

const ShareWithDoctorButton = ({ className = "", label = "Share with my doctor", testId = "share-with-doctor-btn" }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [profile, setProfile] = useState(null);
    const [publicUrl, setPublicUrl] = useState("");

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/emergency-profile");
            setProfile(data);
            // Public URL is derivable on the client from the qr_token + current origin.
            const origin = window.location.origin.replace(/\/$/, "");
            setPublicUrl(data.qr_token ? `${origin}/emergency/public/${data.qr_token}` : "");
        } catch (err) {
            toast.error("Could not load your emergency profile");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (open) load(); }, [open]);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(publicUrl);
            toast.success("Link copied to clipboard");
        } catch { toast.error("Copy failed"); }
    };

    const onPrint = () => {
        if (!publicUrl) return;
        window.open(publicUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid={testId}
                className={`inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white px-4 py-2 text-sm font-medium text-brand-700 transition hover:border-brand-300 hover:bg-brand-50 dark:border-brand-400/30 dark:bg-transparent dark:text-brand-300 dark:hover:bg-brand-500/10 ${className}`}
            >
                <Share2 className="h-4 w-4" /> {label}
            </button>

            {open && (
                <div className="fixed inset-0 z-[75]" data-testid="share-doctor-modal">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
                    <div className="absolute inset-0 grid place-items-center p-4">
                        <div className="w-full max-w-md overflow-hidden rounded-3xl border border-zinc-100 bg-white shadow-2xl dark:border-white/10 dark:bg-zinc-900">
                            <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-4 dark:border-white/5">
                                <div className="flex items-center gap-2">
                                    <span className="grid h-9 w-9 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-violet-500 text-white">
                                        <Share2 className="h-4 w-4" />
                                    </span>
                                    <div>
                                        <p className="label-uppercase">Share</p>
                                        <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">With your doctor</h3>
                                    </div>
                                </div>
                                <button onClick={() => setOpen(false)} className="rounded-full p-2 text-zinc-400 hover:bg-zinc-50 dark:hover:bg-white/5" aria-label="Close" data-testid="share-doctor-close">
                                    <X className="h-4 w-4" />
                                </button>
                            </header>
                            <div className="space-y-4 p-5">
                                {loading ? (
                                    <div className="grid h-32 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></div>
                                ) : (
                                    <>
                                        <p className="text-sm text-zinc-600 dark:text-zinc-300">
                                            This read-only link shows your <strong>blood group</strong>, <strong>allergies</strong>, <strong>chronic conditions</strong>, <strong>current medications summary</strong> and <strong>emergency contacts</strong> (phone numbers masked).
                                        </p>
                                        {publicUrl ? (
                                            <div className="space-y-2 rounded-2xl border border-zinc-200 bg-zinc-50/60 p-3 dark:border-white/10 dark:bg-white/[0.03]">
                                                <p className="break-all font-mono text-[11px] text-zinc-700 dark:text-zinc-300" data-testid="share-doctor-url">{publicUrl}</p>
                                                <div className="flex flex-wrap gap-2">
                                                    <button onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600" data-testid="share-doctor-copy"><Copy className="h-3.5 w-3.5" /> Copy link</button>
                                                    <button onClick={onPrint} className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-white/10 dark:bg-transparent dark:text-zinc-200 dark:hover:bg-white/5" data-testid="share-doctor-open"><ExternalLink className="h-3.5 w-3.5" /> Open</button>
                                                    <button onClick={onPrint} className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-white/10 dark:bg-transparent dark:text-zinc-200 dark:hover:bg-white/5" data-testid="share-doctor-print"><Printer className="h-3.5 w-3.5" /> Print</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200">
                                                Fill in your emergency profile first to enable sharing.
                                            </div>
                                        )}
                                        <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                                            Tip: this link works without login. Anyone with it can view your shared health summary, so share carefully.
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ShareWithDoctorButton;
